import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import AppShell from './components/layout/AppShell';
import Login from './components/Login';
import LandingPage from './pages/LandingPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import CycleDetail from './pages/admin/CycleDetail';
import CommunicatorDashboard from './pages/communicator/CommunicatorDashboard';
import PresentView from './pages/communicator/PresentView';
import UserManagement from './pages/admin/UserManagement';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Login />;
  return children;
}

function RequireAdmin({ children }) {
  const { isAdmin, impersonating, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAdmin || impersonating) return <Navigate to="/communicator" replace />;
  return children;
}

function CommunicatorGate({ children }) {
  const { isCommunicator, isAdmin, impersonating, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (isAdmin && !isCommunicator && !impersonating) return <Navigate to="/admin" replace />;
  // Everyone else may enter: Firestore rules limit what they read (e.g. managers see only their team).
  return children;
}

function DefaultRedirect() {
  const { isAdmin, isCommunicator, isPeoplePartner, impersonating } = useAuth();
  if (isAdmin && impersonating) return <Navigate to="/communicator" replace />;
  if (isPeoplePartner && impersonating) return <Navigate to="/communicator" replace />;
  if (isAdmin) return <Navigate to="/admin" replace />;
  if (isCommunicator) return <Navigate to="/communicator" replace />;
  if (isPeoplePartner) return <Navigate to="/communicator" replace />;
  return <Navigate to="/communicator" replace />;
}

function AuthRedirectOrLogin() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <DefaultRedirect />;
  return <Login />;
}

export default function App() {
  return (
    <Routes>
      {/* Public landing / login — redirects if already signed in */}
      <Route path="/" element={<AuthRedirectOrLogin />} />
      <Route path="/login" element={<AuthRedirectOrLogin />} />

      {/* Authenticated routes */}
      <Route
        path="*"
        element={
          <RequireAuth>
            <Routes>
              {/* Present view has no shell (fullscreen) */}
              <Route path="/communicator/present/:cycleId/:compId" element={<PresentView />} />

              {/* All other routes in the app shell */}
              <Route
                path="*"
                element={
                  <AppShell>
                    <Routes>
                      {/* Admin */}
                      <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
                      <Route path="/admin/cycles/:cycleId" element={<RequireAdmin><CycleDetail /></RequireAdmin>} />
                      <Route path="/admin/users" element={<RequireAdmin><UserManagement /></RequireAdmin>} />

                      {/* Communicator */}
                      <Route path="/communicator" element={<CommunicatorGate><CommunicatorDashboard /></CommunicatorGate>} />

                      {/* Default */}
                      <Route path="*" element={<DefaultRedirect />} />
                    </Routes>
                  </AppShell>
                }
              />
            </Routes>
          </RequireAuth>
        }
      />
    </Routes>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-accent-500 to-cyan-500 flex items-center justify-center animate-pulse">
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-sm text-navy-400 font-medium">Loading…</p>
      </div>
    </div>
  );
}
